import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { EllipsisHorizontalIcon } from "@heroicons/react/24/outline";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import SelectVendors from "components/ui/selectors/SelectVendors";
import SelectCustomers from "components/ui/selectors/SelectCustomers";
import { Table, THead, TBody, TR, TH, TD } from "components/ui/Table";
import { useGenerateReconId } from "http-hooks/paymentreconmain/paymentreconmain";
import { formatDateAndTime } from "utils/date";
import { toIndianCurrencyFormat } from "utils/amount";
import useFullScreenLayout from "hooks/useFullScreenLayout";
import DocumentDetailModal from "./DocumentDetailModal";
import { usePaymentRecon } from "hooks/paymentRecon";

const typeOptions = [
  { value: "P", label: "Vendor" },
  { value: "S", label: "Customer" }
];

const reportParameterOptions = [
  { value: "D", label: "Default" },
  { value: "B", label: "B2B" }
];

export default function SetPaymentRecon() {
  useFullScreenLayout();

  const { id: encodedId } = useParams();
  const id = encodedId ? decodeURIComponent(encodedId) : null;
  const isEditMode = !!id && id !== "new";
  const navigate = useNavigate();
  
  // Get all state and actions from custom hook
  const {
    // State
    reconRecords,
    selectedVendor,
    selectedCustomer,
    reportParameter,
    selectedType,
    debitDocuments,
    creditDocuments,
    debitTotal,
    creditTotal,
    balance,
    isLoadingDocs,
    detailModalOpen,
    selectedDocType,
    selectedDocId,
    agttp,
    existingData,
    isLoadingExisting,

    // Actions
    updateReconRecords,
    handleInputChange,
    setSelectedVendor,
    setSelectedCustomer,
    setSelectedType,
    handleReportParameterChange,
    handleTypeChange,
    handleUpdate,
    toggleDocumentSelection,
    updateDocumentAmount,
    updateDocumentRemark,
    openDetailModal,
    closeDetailModal,
    toggleRemarkVisibility,
    handleSubmit,
    isSubmitting,
  } = usePaymentRecon();
  
  // Fetch recon ID for new entries
  // agttp=0: API returns previousReconId (current max) -- next ID generated server-side on submit
  // agttp=1: pass rectp (P or S), API returns nextReconId -- used as-is on submit
  const reconIdParams = agttp === 1 && selectedType?.value
    ? { rectp: selectedType.value }
    : {};
  const { data: reconIdData } = useGenerateReconId(reconIdParams);
  
  useEffect(() => {
    if (!isEditMode && reconIdData) {
      if (agttp === 0) {
        updateReconRecords({
          previousReconId: reconIdData.previousReconId || ""
        });
      } else if (agttp === 1 && reconIdData.nextReconId) {
        updateReconRecords({
          reconid: reconIdData.nextReconId
        });
      }
    }
  }, [reconIdData, isEditMode, agttp, updateReconRecords]);

  // Pre-populate form in edit mode
  useEffect(() => {
    if (isEditMode && existingData && !isLoadingExisting) {
      // Set form records
      updateReconRecords({
        reconid: existingData.reconid || "",
        recondate: existingData.recdate || "",  // Note: backend sends 'recdate', frontend uses 'recondate'
        remark: existingData.remark || "",
        type: existingData.rectp || ""
      });

      // Set vendor and customer (will be set as read-only in UI)
      if (existingData.customer) {
        setSelectedCustomer({
          ps_Id: existingData.custid,
          name: existingData.customer.name
        });
      }
      if (existingData.vendor) {
        setSelectedVendor({
          ps_Id: existingData.vendid,
          name: existingData.vendor.name
        });
      }

      // Set type for traders
      if (agttp === 1 && existingData.rectp) {
        const typeOption = typeOptions.find(opt => opt.value === existingData.rectp);
        if (typeOption) {
          setSelectedType(typeOption);
        }
      }
    }
  }, [isEditMode, existingData, isLoadingExisting, agttp, updateReconRecords, setSelectedCustomer, setSelectedVendor, setSelectedType]);

  // Auto-load documents when vendor/customer are set in edit mode
  useEffect(() => {
    if (isEditMode && selectedVendor && selectedCustomer && !isLoadingExisting) {
      // Automatically fetch documents when in edit mode and parties are loaded
      handleUpdate();
    }
  }, [isEditMode, selectedVendor, selectedCustomer, isLoadingExisting, handleUpdate]);

  const handleBack = () => {
    navigate("/paymentrecon");
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-semibold leading-6 text-gray-900">
            {isEditMode ? "Edit" : "New"} Payment Reconciliation
          </h1>
          <button
            type="button"
            onClick={handleBack}
            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            Back to List
          </button>
        </div>
      </div>

      <form className="space-y-6">
        {/* Basic Information Section */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-base font-semibold leading-7 text-gray-900 mb-4">
            Basic Information
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {/* Type - Only for Traders */}
            {agttp === 1 && (
              isEditMode ? (
                <Input
                  label="Type"
                  value={selectedType?.label || ""}
                  readOnly={true}
                />
              ) : (
                <Select
                  label="Type"
                  options={typeOptions}
                  value={selectedType}
                  onChange={handleTypeChange}
                  placeholder="Choose here"
                  isRequired={true}
                />
              )
            )}

            {/* Report Parameter - Only for General and not in edit mode */}
            {agttp === 0 && !isEditMode && (
              <Select
                label="Report Parameter"
                options={reportParameterOptions}
                value={reportParameter}
                onChange={handleReportParameterChange}
              />
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* agttp=0 create: show previous (current max) ID. agttp=1 create / edit: show actual ID */}
            {agttp === 0 && !isEditMode ? (
              <Input
                label="Previous Recon ID"
                value={reconRecords.previousReconId}
                placeholder="Previous Recon ID"
                readOnly={true}
              />
            ) : (
              <Input
                label="ID"
                value={reconRecords.reconid}
                placeholder="Recon ID"
                readOnly={true}
              />
            )}
            
            <Input
              label="Date"
              type="date"
              name="recondate"
              value={reconRecords.recondate}
              onChange={handleInputChange}
              isRequired={true}
              now={!id}
              withinFY={true}
            />
          </div>

          {/* Vendor/Customer Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* For General (agttp=0): Show both fields, but vendor field changes based on Report Parameter */}
            {agttp === 0 && (
              <>
                {isEditMode ? (
                  <Input
                    label="Customer"
                    value={selectedCustomer?.name || ""}
                    readOnly={true}
                  />
                ) : (
                  <SelectCustomers
                    label="Customer"
                    value={selectedCustomer}
                    onChange={setSelectedCustomer}
                    placeholder="Select Customer"
                    isRequired={true}
                  />
                )}
                
                {/* B2B: Show customers in vendor field, Default: Show vendors */}
                {isEditMode ? (
                  <Input
                    label="Vendor"
                    value={selectedVendor?.name || ""}
                    readOnly={true}
                  />
                ) : reportParameter?.value === 'B' ? (
                  <SelectCustomers
                    label="Vendor (B2B)"
                    value={selectedVendor}
                    onChange={setSelectedVendor}
                    placeholder="Select Customer"
                    isRequired={true}
                  />
                ) : (
                  <SelectVendors
                    label="Vendor"
                    value={selectedVendor}
                    onChange={setSelectedVendor}
                    placeholder="Select Vendor"
                    isRequired={true}
                  />
                )}
              </>
            )}

            {/* For Traders (agttp=1): Show based on type */}
            {agttp === 1 && selectedType && (
              <>
                {selectedType.value === "S" && (
                  isEditMode ? (
                    <Input
                      label="Customer"
                      value={selectedCustomer?.name || ""}
                      readOnly={true}
                    />
                  ) : (
                    <SelectCustomers
                      label="Customer"
                      value={selectedCustomer}
                      onChange={setSelectedCustomer}
                      placeholder="Select Customer"
                      isRequired={true}
                    />
                  )
                )}
                
                {selectedType.value === "P" && (
                  isEditMode ? (
                    <Input
                      label="Vendor"
                      value={selectedVendor?.name || ""}
                      readOnly={true}
                    />
                  ) : (
                    <SelectVendors
                      label="Vendor"
                      value={selectedVendor}
                      onChange={setSelectedVendor}
                      placeholder="Select Vendor"
                      isRequired={true}
                    />
                  )
                )}
              </>
            )}
          </div>

          <div className="flex justify-center mt-4">
            <button
              type="button"
              onClick={handleUpdate}
              disabled={isLoadingDocs}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:bg-gray-400"
            >
              {isLoadingDocs ? "Loading..." : "Update Bills"}
            </button>
          </div>
        </div>

        {/* Bills Display Section - Two Column Layout */}
        {(debitDocuments.length > 0 || creditDocuments.length > 0) && (
          <div className="border-b border-gray-200 pb-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Debit Column (Left) */}
              <div>
                <h3 className="text-lg font-semibold text-center mb-4">Debit</h3>
                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                  <Table>
                    <THead>
                      <TR>
                        <TH first className="w-10">
                          <span className="sr-only">Select</span>
                        </TH>
                        <TH>Type</TH>
                        <TH>Doc No</TH>
                        <TH>Date</TH>
                        <TH last className="text-right">Amount</TH>
                      </TR>
                    </THead>
                    <TBody>
                      {debitDocuments.map((doc) => (
                        <>
                          <TR key={`debit-${doc.docid}`}>
                            <TD first>
                              <Input
                                type="checkbox"
                                showLabel={false}
                                value={doc.isSelected}
                                onChange={() => toggleDocumentSelection(doc.docid, true)}
                              />
                            </TD>
                            <TD>{doc.doctype}</TD>
                            <TD>
                              <button
                                type="button"
                                onClick={() => openDetailModal(doc.doctype, doc.docid)}
                                className="text-indigo-600 hover:text-indigo-900 underline"
                              >
                                {doc.docno}
                              </button>
                            </TD>
                            <TD>{formatDateAndTime(doc.docdate)}</TD>
                            <TD last className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={doc.amount}
                                  onChange={(e) => updateDocumentAmount(doc.docid, e.target.value, true)}
                                  className="w-24 text-right text-sm"
                                  showLabel={false}
                                />
                                <button
                                  type="button"
                                  onClick={() => toggleRemarkVisibility(doc.docid)}
                                  className="text-gray-400 hover:text-gray-600"
                                  title="Toggle Remark"
                                >
                                  <EllipsisHorizontalIcon className="h-5 w-5" />
                                </button>
                              </div>
                            </TD>
                          </TR>
                          {doc.showRemark && (
                            <TR key={`debit-remark-${doc.docid}`}>
                              <TD first colSpan={5} last className="bg-gray-50">
                                <Input
                                  placeholder="Enter remark"
                                  value={doc.remark}
                                  onChange={(e) => updateDocumentRemark(doc.docid, e.target.value, true)}
                                  maxLength={50}
                                  showLabel={false}
                                />
                              </TD>
                            </TR>
                          )}
                        </>
                      ))}
                    </TBody>
                  </Table>
                </div>
              </div>

              {/* Credit Column (Right) */}
              <div>
                <h3 className="text-lg font-semibold text-center mb-4">Credit</h3>
                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                  <Table>
                    <THead>
                      <TR>
                        <TH first className="w-10">
                          <span className="sr-only">Select</span>
                        </TH>
                        <TH>Type</TH>
                        <TH>Doc No</TH>
                        <TH>Date</TH>
                        <TH last className="text-right">Amount</TH>
                      </TR>
                    </THead>
                    <TBody>
                      {creditDocuments.map((doc) => (
                        <>
                          <TR key={`credit-${doc.docid}`}>
                            <TD first>
                              <Input
                                type="checkbox"
                                showLabel={false}
                                value={doc.isSelected}
                                onChange={() => toggleDocumentSelection(doc.docid, false)}
                              />
                            </TD>
                            <TD>{doc.doctype}</TD>
                            <TD>
                              <button
                                type="button"
                                onClick={() => openDetailModal(doc.doctype, doc.docid)}
                                className="text-indigo-600 hover:text-indigo-900 underline"
                              >
                                {doc.docno}
                              </button>
                            </TD>
                            <TD>{formatDateAndTime(doc.docdate)}</TD>
                            <TD last className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={doc.amount}
                                  onChange={(e) => updateDocumentAmount(doc.docid, e.target.value, false)}
                                  className="w-24 text-right text-sm"
                                  showLabel={false}
                                />
                                <button
                                  type="button"
                                  onClick={() => toggleRemarkVisibility(doc.docid)}
                                  className="text-gray-400 hover:text-gray-600"
                                  title="Toggle Remark"
                                >
                                  <EllipsisHorizontalIcon className="h-5 w-5" />
                                </button>
                              </div>
                            </TD>
                          </TR>
                          {doc.showRemark && (
                            <TR key={`credit-remark-${doc.docid}`}>
                              <TD first colSpan={5} last className="bg-gray-50">
                                <Input
                                  placeholder="Enter remark"
                                  value={doc.remark}
                                  onChange={(e) => updateDocumentRemark(doc.docid, e.target.value, false)}
                                  maxLength={50}
                                  showLabel={false}
                                />
                              </TD>
                            </TR>
                          )}
                        </>
                      ))}
                    </TBody>
                  </Table>
                </div>
              </div>
            </div>

            {/* Totals Row */}
            <div className="mt-6 border-t-2 border-gray-900 pt-4">
              <div className="grid grid-cols-3 gap-4 items-center mb-4">
                <div className="text-right">
                  <span className="text-sm font-medium">Debit Total: </span>
                  <span className="text-lg font-semibold">{toIndianCurrencyFormat(debitTotal, true)}</span>
                </div>
                <div className="text-center text-xl font-bold">
                  TOTAL
                </div>
                <div className="text-left">
                  <span className="text-sm font-medium">Credit Total: </span>
                  <span className="text-lg font-semibold">{toIndianCurrencyFormat(creditTotal, true)}</span>
                </div>
              </div>
              
              {/* Balance Row */}
              <div className="border-t border-gray-300 pt-4">
                <div className="flex justify-center items-center">
                  <span className="text-base font-medium mr-3">Balance (Debit - Credit):</span>
                  <span className={`text-xl font-bold ${balance > 0 ? 'text-green-600' : balance < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                    {toIndianCurrencyFormat(balance, true)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Remarks Section */}
        <div className="border-b border-gray-200 pb-6">
          <Input
            label="Remarks"
            type="textarea"
            name="remark"
            value={reconRecords.remark}
            onChange={handleInputChange}
            placeholder="Enter remarks"
            maxLength={100}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="rounded-md bg-gray-600 px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (isEditMode ? "Updating..." : "Submitting...") : (isEditMode ? "Update" : "Submit")}
          </button>
        </div>
      </form>

      {/* Document Detail Modal */}
      <DocumentDetailModal
        isOpen={detailModalOpen}
        onClose={closeDetailModal}
        docType={selectedDocType}
        docId={selectedDocId}
      />
    </div>
  );
}
